import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * FK option lists for the schema-driven create/edit forms (§1.6).
 *
 * `Form` renders an FK as a picker only when the consumer hands it options —
 * otherwise the field falls through to a raw text input with an "FK: clients"
 * helper line, i.e. the operator is asked to type a UUID. These hooks are the
 * supply side for the invoice forms.
 *
 * ⚠️ The bare list endpoint caps at 500 rows (`crud.js` — the frozen list
 * contract). A picker that silently drops row 501 is worse than no picker: the
 * operator searches, finds nothing, and concludes the record does not exist. So
 * the hooks report `capped` and callers say so on screen rather than pretending
 * the list is complete.
 */

/** Matches the bare-list LIMIT in src/routes/crud.js. */
export const LIST_CAP = 500;

export interface FkOption { value: string; label: string }

interface ClientRow { id: string; name: string | null; short_name: string | null; status: string | null }
interface PropertyRow { id: string; name: string | null; location_code: string | null; status: string | null }

export interface FkOptionsResult {
  options: FkOption[];
  /** True when the response came back at the list cap, so rows may be missing. */
  capped: boolean;
  isLoading: boolean;
}

/** Every client in the org, as `Name (short name)` options. */
export function useClientOptions(enabled = true): FkOptionsResult {
  const query = useQuery<ClientRow[]>({
    queryKey: ['fk-options', 'clients'],
    queryFn: ({ signal }) => api.get<ClientRow[]>('/api/clients', signal),
    enabled,
    staleTime: 5 * 60_000,
  });
  const rows = query.data ?? [];
  return {
    options: rows.map(c => ({
      value: c.id,
      label: c.short_name && c.name && c.short_name !== c.name
        ? `${c.name} (${c.short_name})`
        : (c.name || c.short_name || c.id.slice(0, 8)),
    })),
    capped: rows.length >= LIST_CAP,
    isLoading: query.isLoading,
  };
}

/**
 * Properties belonging to ONE client. Deliberately client-scoped rather than
 * org-wide: an invoice's property must sit under the invoice's client, so an
 * unscoped picker would offer choices that make the row bill the right client
 * for someone else's building — the same cross-client defect the line-item
 * endpoint refuses server-side.
 */
export function usePropertyOptionsForClient(clientId: string | undefined, enabled = true): FkOptionsResult {
  const query = useQuery<PropertyRow[]>({
    queryKey: ['fk-options', 'properties', clientId],
    queryFn: ({ signal }) => api.get<PropertyRow[]>(`/api/properties?client_id=${clientId}`, signal),
    enabled: enabled && !!clientId,
    staleTime: 5 * 60_000,
  });
  const rows = query.data ?? [];
  return {
    options: rows
      .filter(p => (p.status ?? 'active') !== 'inactive')
      .map(p => ({
        value: p.id,
        label: p.location_code ? `${p.name || 'Property'} — ${p.location_code}` : (p.name || p.id.slice(0, 8)),
      })),
    capped: rows.length >= LIST_CAP,
    isLoading: query.isLoading,
  };
}
